import { useCallback, useMemo, useEffect } from 'react';
import { Claim } from '../types';
import { useUrlParam } from '../CiterightContextProvider';

interface UseClaimNavigationProps {
  claims: Claim[] | undefined;
  onNavigate?: (claimId: string) => void;
}

interface UseClaimNavigationReturn {
  currentClaimIndex: number;
  currentClaimId: string | null;
  setClaimIndex: (index: number) => void;
  setClaimById: (claimId: string) => void;
  goToMostImportant: () => void;
  goToNext: () => void;
  goToPrevious: () => void;
}

/**
 * Hook for managing claim navigation state.
 * Stores the current claim ID and derives the index from sorted claims.
 */
export const useClaimNavigation = ({
  claims,
  onNavigate,
}: UseClaimNavigationProps): UseClaimNavigationReturn => {
  // URL param for claim deep linking (uses replaceState to not fill history)
  const { value: currentClaimId, setValue: setClaimIdParam } = useUrlParam(
    'claim_id',
    { replace: true },
  );

  useEffect(
    () => {
      if (onNavigate && currentClaimId) {
        onNavigate(currentClaimId);
      }
    },
    // we only want to run this effect when the component mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Wrapper to update URL and call onNavigate
  const setCurrentClaimId = useCallback(
    (claimId: string | null) => {
      setClaimIdParam(claimId);
      if (claimId && onNavigate) {
        onNavigate(claimId);
      }
    },
    [onNavigate, setClaimIdParam],
  );

  // Sort claims by document order (text_start_index)
  const sortedClaims = useMemo(() => {
    if (!claims) return [];
    return [...claims].sort(
      (a, b) =>
        (a.text_start_index ?? Infinity) - (b.text_start_index ?? Infinity),
    );
  }, [claims]);

  const claimsLength = sortedClaims.length;

  // Derive current claim index from ID
  const currentClaimIndex = useMemo(() => {
    if (!currentClaimId) return -1;
    const index = sortedClaims.findIndex((c) => c.id === currentClaimId);
    return index;
  }, [sortedClaims, currentClaimId]);

  // Set claim by index (looks up ID in sorted claims)
  const setClaimIndex = useCallback(
    (index: number) => {
      if (index < 0 || index >= claimsLength) {
        return;
      }
      setCurrentClaimId(sortedClaims[index]?.id ?? null);
    },
    [sortedClaims, claimsLength, setCurrentClaimId],
  );

  // Set claim directly by ID
  const setClaimById = useCallback(
    (claimId: string) => {
      setCurrentClaimId(claimId);
    },
    [setCurrentClaimId],
  );

  // Navigation handlers
  const goToMostImportant = useCallback(() => {
    // Use original order (first claim is most important)
    if (claims && claims.length > 0) {
      setCurrentClaimId(claims[0]?.id ?? null);
    }
  }, [claims, setCurrentClaimId]);

  const goToNext = useCallback(() => {
    if (currentClaimIndex >= 0 && currentClaimIndex < claimsLength - 1) {
      setCurrentClaimId(sortedClaims[currentClaimIndex + 1]?.id ?? null);
    }
  }, [sortedClaims, currentClaimIndex, claimsLength, setCurrentClaimId]);

  const goToPrevious = useCallback(() => {
    if (currentClaimIndex > 0) {
      setCurrentClaimId(sortedClaims[currentClaimIndex - 1]?.id ?? null);
    }
  }, [sortedClaims, currentClaimIndex, setCurrentClaimId]);

  return {
    currentClaimIndex,
    currentClaimId,
    setClaimIndex,
    setClaimById,
    goToMostImportant,
    goToNext,
    goToPrevious,
  };
};

export default useClaimNavigation;
